<!--
	@name app-source-result-mortgage-item-name
	@description Lifetime mortgage product item name
	@date 2022/02/21
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<div :component="$options.name">
		<div class="text-weight-medium font-weight-bold mb-1 include-2">
			{{ item.provider.name }}
		</div>
		<span class="text-body-2 font-weight-bold">
			{{ item.name }}
		</span>
		<div class="d-block">
			<v-tooltip v-if="preapproval" bottom max-width="600" :disabled="!preapprovalMessage">
				<template #activator="{ on }">
					<v-chip
						class="mr-2 mt-2"
						:color="preapprovalColor"
						dark
						label
						x-small
						active
						style="border: 1px solid rgba(0, 0, 0, 0.25) !important; height: 18px !important"
						v-on="on"
						@click="openFullText"
					>
						Pre-approval
					</v-chip>
				</template>
				<span>{{ preapprovalMessage }}</span>
			</v-tooltip>

			<v-tooltip v-if="item.enhancedImpairedLife" bottom>
				<template #activator="{ on }">
					<v-chip
						class="mr-2 mt-2 black--text"
						color="success lighten-3"
						label
						x-small
						style="border: 1px solid rgba(0, 0, 0, 0.25) !important; height: 18px !important"
						v-on="on"
					>
						Medical Underwriting
					</v-chip>
				</template>
				<span>Product requires a medical questionnaire to be completed</span>
			</v-tooltip>

			<v-chip
				v-if="item.preview"
				class="mr-2 mt-2"
				color="warning accent-3"
				label
				x-small
				active
				style="border: 1px solid rgba(0, 0, 0, 0.25) !important; height: 18px !important"
			>
				PREVIEW
			</v-chip>
			<v-chip
				v-if="item.loanPaymentTypeName"
				class="mr-2 mt-2 font-weight-regular"
				color="blue-grey lighten-4"
				text-color="black"
				label
				x-small
				active
				style="border: 1px solid rgba(0, 0, 0, 0.25) !important; height: 18px !important"
			>
				{{ item.loanPaymentTypeName }}
			</v-chip>
			<v-chip
				v-if="item.earlyRepaymentChargeIsFixed"
				class="mr-2 mt-2 font-weight-regular"
				color="blue-grey lighten-4"
				text-color="black"
				label
				x-small
				active
				style="border: 1px solid rgba(0, 0, 0, 0.25) !important; height: 18px !important"
			>
				Fixed ERC
			</v-chip>
			<v-chip
				v-if="item.incomeAssessed"
				class="mr-2 mt-2 font-weight-regular"
				color="blue-grey lighten-4"
				text-color="black"
				label
				x-small
				active
				style="border: 1px solid rgba(0, 0, 0, 0.25) !important; height: 18px !important"
			>
				Income Assessed
			</v-chip>
			<v-chip
				v-if="item.mandatoryPayments"
				class="mr-2 mt-2 font-weight-regular"
				color="blue-grey lighten-4"
				text-color="black"
				label
				x-small
				active
				style="border: 1px solid rgba(0, 0, 0, 0.25) !important; height: 18px !important"
			>
				Mandatory Payments
			</v-chip>

			<div v-if="item.procurationFee.length">
				<span v-for="(fee, index) in item.procurationFee" :key="index">
					<v-tooltip bottom>
						<template #activator="{ on }">
							<v-chip
								class="mr-2 mt-2 font-weight-regular"
								color="blue-grey lighten-3"
								text-color="black"
								label
								x-small
								active
								style="border: 1px solid rgba(0, 0, 0, 0.25) !important; height: 18px !important"
								v-on="on"
							>
								{{ fee.name }}
							</v-chip>
						</template>
						<span> Proc Fee: {{ fee.amount | numFormat('0,0.00') }}% </span>
					</v-tooltip>
				</span>
			</div>
		</div>

		<common-dialog ref="fullTextDialog" :max-width="800" :component="$options.name" :color="preapprovalColor">
			<template #header> Pre-approval </template>
			<template #body>
				<p v-if="item.preapproval?.message" class="nlbr text-body-2 mb-0">
					{{ item.preapproval.message }}
				</p>
			</template>
		</common-dialog>
	</div>
</template>

<script>
	import CommonDialog from '@/component/common/dialog';

	export default {
		name: 'app-source-result-mortgage-item-name',

		components: { CommonDialog },

		props: {
			item: { type: Object, required: true }
		},

		data() {
			return {
				truncateCharacterCount: 200
			};
		},

		computed: {
			preapproval() {
				return this.item.preapproval?.outcome;
			},

			preapprovalRating() {
				if (!this.preapproval) return false;
				if (this.item.preapproval.outcome === 'No Lend') return 1;
				if (this.item.preapproval.outcome === 'Refer') return 2;
				return 3;
			},

			preapprovalMessage() {
				if (!this.preapproval) return false;
				if (!this.item.preapproval?.message || this.item.preapproval.message == '') return false;
				if (this.item.preapproval.message.length > this.truncateCharacterCount) return 'Click for full text';
				return this.item.preapproval.message;
			},

			preapprovalColor() {
				return this.preapprovalRating === 3 ? 'success' : this.preapprovalRating === 2 ? 'warning' : 'error';
			}
		},

		created() {},

		methods: {
			/**
			 * @name procurationFeeChipColor
			 * @description Get Procuration Fee chip color based on type
			 * @param {String} type Procuration Fee type
			 * @return {String} color
			 */
			procurationFeeChipColor(type) {
				if (type === 'club') return 'rgba(30, 176, 225, 1)';
				else if (type === 'academy') return 'rgba(27, 120, 216, 1)';
				else if (type === 'platinum') return 'blue-grey lighten-1';
				else if (type === 'platinumPlus') return 'blue-grey';
				else if (type === 'elite') return 'blue-grey darken-1';
			},

			/**
			 * @name open
			 * @description Open dialog and set properties
			 */
			openFullText() {
				this.$refs.fullTextDialog.open();
			}
		}
	};
</script>

<style scoped lang="scss">
	.include-2 {
		display: none;
	}

	@media (max-width: 1235px) {
		.include-2 {
			display: block;
		}
	}
</style>
